import { renderToastLabel } from "./undoStack";

/**
 * Text in the email archive toast: plain, or with the name of the task or
 * sub-task the email became as a link to it — the same link every other
 * "created" toast carries (see {@link renderToastLabel}).
 */
export type ArchiveToastText =
  | string
  | { prefix?: string; label: string; suffix?: string; onClick: () => void };

export interface ArchiveToastLabels {
  /** Shown next to Undo while the archive waits out the undo window. */
  pendingLabel?: ArchiveToastText;
  /** Replaces it once Gmail confirms the archive. */
  successLabel?: ArchiveToastText;
}

/** One label inside the archive toast whose id is `toastId`. */
export function renderArchiveToastText(text: ArchiveToastText, toastId: string) {
  return typeof text === "string" ? text : renderToastLabel({ ...text, toastId });
}

/** The caller's labels, or the plain "Archiving email" ones. */
export function archiveToastLabels(count: number, labels?: ArchiveToastLabels) {
  return {
    pending:
      labels?.pendingLabel ??
      (count === 1 ? "Archiving email" : `Archiving ${count} emails`),
    success:
      labels?.successLabel ??
      (count === 1 ? "Email archived" : `${count} emails archived`),
  };
}

/**
 * Labels for an email that became something (a task, a sub-task, an event)
 * and is now being archived. `created` names what was made; `archived` names
 * what goes: `Task "Pay rent" created · archiving email`, then
 * `Task "Pay rent" created and email archived`.
 */
export function createdFromEmailLabels(
  created: ArchiveToastText,
  archived: string,
): Required<ArchiveToastLabels> {
  if (typeof created === "string") {
    return {
      pendingLabel: `${created} · archiving ${archived}`,
      successLabel: `${created} and ${archived} archived`,
    };
  }
  const before = created.suffix ?? "";
  return {
    pendingLabel: { ...created, suffix: `${before} · archiving ${archived}` },
    successLabel: { ...created, suffix: `${before} and ${archived} archived` },
  };
}
